import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ArtikelCombo } from "@/components/SalesArtikelCombo";
import { SignaturePad } from "@/components/SignaturePad";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, Printer, Building2, FileText, Package, PenLine } from "lucide-react";

const COMPANY = {
  naam: "Clazing Convenience Food B.V.", adres: "Struikhei 20", postplaats: "4714 VB Sprundel",
  tel: "0165-726000", email: "John@clazingconvenience.nl", kvk: "24334659", plaats: "Sprundel",
};
const OFFERTE_STATUS = ["Concept", "Verstuurd", "Geaccepteerd", "Afgewezen"];
const OFF_BADGE = { Concept: "bg-slate-100 text-slate-600", Verstuurd: "bg-blue-100 text-blue-700", Geaccepteerd: "bg-emerald-100 text-emerald-700", Afgewezen: "bg-rose-100 text-rose-700" };
const PRIJSBASIS = [["kg", "Per kg"], ["stuk", "Per stuk"], ["doos", "Per doos"]];
const basisLabel = (b) => (PRIJSBASIS.find((x) => x[0] === b) || PRIJSBASIS[2])[1];
const DEFAULT_INTRO = "Naar aanleiding van ons prettige gesprek, doe ik u deze offerte toekomen. Bijgaand treft u ons voorstel voor het door u aangevraagde product.\n\nVoor vragen en opmerkingen over deze offerte kunt u ons bereiken onder telefoonnummer 0165-726000.\n\nHopende u hiermee een interessante aanbieding te hebben gedaan en in afwachting van uw reactie, verblijf ik";
const DEFAULT_TERMS = "Prijs is geleverd.\nAanpassingen van de verpakking kunnen van invloed zijn op de prijs.\nDe prijzen zijn gebaseerd op betaling binnen 30 dagen.\nDeze offerte is 4 weken geldig na offertedatum.\n** Prijzen excl. BTW";
const PRIVATE_OFFERTE_AUTHORS = new Set([
  "john@clazingconvenience.nl",
  "marc@clazing.nl",
]);
const MANAGEMENT_ROLES = new Set(["Bedrijfsleiding", "Bedrijfsleiding 2"]);

const num = (v) => Number(v) || 0;
const eur = (n) => "€ " + num(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const eur4 = (n) => "€ " + num(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const kgf = (n) => num(n).toLocaleString("nl-NL", { maximumFractionDigits: 3 }) + " kg";
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const fmtLong = (v) => (v ? new Date(v).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" }) : "");
const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// —— berekeningen per regel ——
const nettoDoos = (r) => num(r.stuksPerDoos) * num(r.kgPerStuk);
const doosPrijs = (r) => {
  const basis = r.prijsBasis || "doos";
  if (basis === "kg") return num(r.prijsPerKg) * nettoDoos(r);
  if (basis === "stuk") return num(r.prijsPerStuk) * num(r.stuksPerDoos);
  return num(r.prijsPerDoos);
};
const nettoPallet = (r) => nettoDoos(r) * num(r.dozenPerPallet);
const aantalDozen = (r) => (r.pallets !== "" && r.pallets != null ? num(r.pallets) * num(r.dozenPerPallet) : num(r.aantal));
const effKgPrijs = (r) => (nettoDoos(r) > 0 ? doosPrijs(r) / nettoDoos(r) : num(r.prijsPerKg));
const regelTotaal = (r) => aantalDozen(r) * doosPrijs(r);
const totaal = (o) => (o.regels || []).reduce((s, r) => s + regelTotaal(r), 0);
const verpakkingLabel = (r) => {
  const s = num(r.stuksPerDoos), kg = num(r.kgPerStuk);
  if (s && kg) return `${s} × ${kg} kg (${kgf(nettoDoos(r))} netto/doos)`;
  if (s) return `${s} stuks per doos`;
  return "";
};

function nextNummer(offertes) {
  const y = new Date().getFullYear();
  let max = 0;
  offertes.forEach((o) => { const m = String(o.offertenummer || "").match(/^(\d{4})-(\d+)$/); if (m && Number(m[1]) === y) max = Math.max(max, Number(m[2])); });
  return `${y}-${String(max + 1).padStart(3, "0")}`;
}

const HIDE_FIELDS = [
  ["totaal", "Totaalbedrag (onderaan)"],
  ["regelprijs", "Prijs per product/regel"],
  ["prijsKg", "Prijs per kg"],
  ["prijsStuk", "Prijs per stuk"],
  ["prijsDoos", "Prijs per doos"],
  ["minAfname", "Minimale afname / aantallen"],
  ["bijzonderheden", "Bijzonderheden / voorwaarden"],
];

const emptyRegel = {
  artikel: "", folie: "", stuksPerDoos: "", kgPerStuk: "",
  dozenPerPallet: "", palletType: "Europallet",
  prijsBasis: "kg", prijsPerKg: "", prijsPerStuk: "", prijsPerDoos: "",
  pallets: "", aantal: "", opmerking: "",
};

export default function OffertesTab({
  klanten,
  artikelen,
  offertes,
  folies,
  medewerkers,
  user,
  reload,
}) {
  const empty = {
    offertenummer: "", klantCode: "", contactAanhef: "Geachte heer/mevrouw,",
    datum: new Date().toISOString().slice(0, 10), geldigTot: "", status: "Concept",
    ondertekenaar: "J. Hazenbroek", introtekst: DEFAULT_INTRO, bijzonderheden: DEFAULT_TERMS,
    handtekening: "", notitie: "", regels: [], visibility: "shared", allowed_viewer_ids: [],
  };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [zoek, setZoek] = useState("");
  const [orderingId, setOrderingId] = useState(null);
  const [sigKey, setSigKey] = useState(0); // reset canvas on new/edit
  const [hideFor, setHideFor] = useState(null); // offerte in download-dialog
  const [hideOpts, setHideOpts] = useState({});

  const folieList = (folies && folies.length ? folies : ["Blanco", "Mr John", "Clazing"]);
  const klantNaam = (code) => klanten.find((k) => k.code === code)?.naam || code || "—";
  const klant = (code) => klanten.find((k) => k.code === code);
  const userEmail = String(user?.email || "").trim().toLowerCase();
  const isPrivateAuthor = PRIVATE_OFFERTE_AUTHORS.has(userEmail);
  const isManagement = MANAGEMENT_ROLES.has(user?.role);
  const eligibleViewers = (medewerkers || []).filter((medewerker) => (
    medewerker.id !== user?.id && !MANAGEMENT_ROLES.has(medewerker.role)
  ));
  const canEditOfferte = (offerte) => {
    if (offerte.visibility !== "restricted") return true;
    return String(offerte.created_by_id || "") === String(user?.id || "");
  };
  const toggleViewer = (viewerId) => {
    const current = form.allowed_viewer_ids || [];
    const allowed_viewer_ids = current.includes(viewerId)
      ? current.filter((id) => id !== viewerId)
      : [...current, viewerId];
    setForm({ ...form, allowed_viewer_ids });
  };

  const addRegel = () => setForm({ ...form, regels: [...(form.regels || []), { ...emptyRegel }] });
  const setRegel = (i, patch) => setForm({ ...form, regels: form.regels.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const delRegel = (i) => setForm({ ...form, regels: form.regels.filter((_, idx) => idx !== i) });

  const enrich = (r) => ({ ...r, nettoGewichtDoos: nettoDoos(r), nettoGewichtPallet: nettoPallet(r), prijsPerDoos: doosPrijs(r), prijsPerKgEff: effKgPrijs(r), aantal: aantalDozen(r) });

  const save = async () => {
    if (!form.klantCode) return toast.error("Kies een klant.");
    let nummer = form.offertenummer;
    if (!editId) {
      try { nummer = (await api.post("/sales/offerte-nummer")).data.nummer; }
      catch (e) { return toast.error("Kon geen offertenummer ophalen — probeer opnieuw."); }
    }
    const regels = (form.regels || []).map(enrich);
    const body = { ...form, regels, offertenummer: nummer, klantNaam: klantNaam(form.klantCode), totaal: totaal(form) };
    try {
      if (editId) await api.put(`/sales/offertes/${editId}`, body);
      else await api.post("/sales/offertes", body);
      toast.success(`Offerte ${nummer} opgeslagen`); setForm(empty); setEditId(null); setSigKey((k) => k + 1); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (o) => { setEditId(o.id); setForm({ ...empty, ...o, regels: (o.regels || []).map((r) => ({ ...emptyRegel, ...r })) }); setSigKey((k) => k + 1); if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" }); };
  const nieuw = () => { setEditId(null); setForm(empty); setSigKey((k) => k + 1); };
  const del = async (o) => { if (!window.confirm(`Offerte ${o.offertenummer || ""} verwijderen?`)) return; try { await api.delete(`/sales/offertes/${o.id}`); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const maakOrder = async (o) => {
    if (orderingId) return;
    setOrderingId(o.id);
    try {
      await api.post("/sales/orders", { offertenummer: o.offertenummer, klantCode: o.klantCode, klantNaam: o.klantNaam, datum: new Date().toISOString().slice(0, 10), regels: o.regels || [], totaal: o.totaal ?? totaal(o), status: "Nieuw", offerteId: o.id });
      toast.success(`Order aangemaakt uit offerte ${o.offertenummer}`); reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOrderingId(null); }
  };

  const print = (o, hide = o.verbergVelden || {}) => {
    const k = klant(o.klantCode) || {};
    const regels = (o.regels || []).filter((r) => r.artikel);
    const blokken = regels.map((r, idx) => {
      const basis = r.prijsBasis || "doos";
      const kgLabel = nettoDoos(r) > 0 ? `${eur4(effKgPrijs(r))} per kg` : (basis === "kg" && r.prijsPerKg ? `${eur4(r.prijsPerKg)} per kg` : "");
      const stukLabel = basis === "stuk" && r.prijsPerStuk ? `${eur4(r.prijsPerStuk)} per stuk` : "";
      const afnameLabel = num(r.pallets) ? `${num(r.pallets)} pallet(s) — ${aantalDozen(r)} dozen` : (r.aantal ? `${r.aantal} dozen` : "");
      const rows = [
        ["Folie", r.folie],
        ["Verpakking", verpakkingLabel(r)],
        ["Geladen op pallet", r.palletType],
        ["Dozen per pallet", r.dozenPerPallet],
        ["Netto gewicht per pallet", nettoPallet(r) ? kgf(nettoPallet(r)) : ""],
        ["Prijsbasis", basisLabel(basis)],
        hide.prijsKg ? null : ["Prijs per kg", kgLabel],
        hide.prijsStuk ? null : ["Prijs per stuk", stukLabel],
        hide.prijsDoos ? null : ["Prijs per doos", eur(doosPrijs(r))],
        hide.minAfname ? null : ["Minimale afname", afnameLabel],
      ].filter((x) => x && x[1] !== "" && x[1] != null)
        .map(([l, v]) => `<tr><td style="padding:4px 14px 4px 0;color:#64748b;white-space:nowrap;vertical-align:top;font-size:12.5px">${esc(l)}</td><td style="padding:4px 0;font-size:12.5px"><b>${esc(v)}</b></td></tr>`).join("");
      return `<div style="margin:0 0 14px;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden">
        <div style="display:flex;justify-content:space-between;align-items:center;background:#f1f5f9;padding:10px 14px">
          <div style="font-weight:800;color:#0f172a;font-size:14px">${idx + 1}. ${esc(r.artikel)}</div>
          ${hide.regelprijs ? "" : `<div style="font-weight:800;color:#1C64B0;font-size:14px">${eur(regelTotaal(r))}</div>`}
        </div>
        <div style="padding:10px 14px"><table style="border-collapse:collapse">${rows}</table>${r.opmerking ? `<div style="font-size:12px;color:#64748b;margin-top:6px;font-style:italic">${esc(r.opmerking)}</div>` : ""}</div>
      </div>`;
    }).join("");
    const terms = String(o.bijzonderheden || DEFAULT_TERMS).split("\n").filter(Boolean).map((t) => `<li style="margin:2px 0">${esc(t)}</li>`).join("");
    const sig = o.handtekening ? `<img src="${o.handtekening}" style="max-height:70px;max-width:230px;object-fit:contain"/>` : `<div style="height:70px"></div>`;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><meta charset="utf-8"><title>Offerte ${esc(o.offertenummer || "")}</title></head>
    <body style="font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;max-width:760px;margin:0 auto;padding:40px 44px;line-height:1.5">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1C64B0;padding-bottom:16px">
        <img src="/clazing-logo.png" style="height:64px" onerror="this.style.display='none'"/>
        <div style="text-align:right">
          <div style="font-size:30px;font-weight:800;color:#1C64B0;letter-spacing:3px">OFFERTE</div>
          <div style="font-size:13px;color:#64748b;margin-top:2px">Nr. <b style="color:#0f172a">${esc(o.offertenummer || "")}</b></div>
        </div>
      </div>

      <div style="display:flex;justify-content:space-between;margin-top:22px;font-size:13px">
        <div>
          <div style="text-transform:uppercase;font-size:10px;letter-spacing:1px;color:#94a3b8;font-weight:700">Aan</div>
          <div style="margin-top:4px"><b style="font-size:14px">${esc(k.naam || o.klantNaam || "")}</b>${k.contact ? `<br/>T.a.v. ${esc(k.contact)}` : ""}${k.bezoekadres || k.adres ? `<br/>${esc(k.bezoekadres || k.adres)}` : ""}</div>
        </div>
        <div style="text-align:right">
          <div>${esc(COMPANY.plaats)}, ${fmtLong(o.datum)}</div>
          <table style="margin-top:6px;font-size:12.5px;margin-left:auto">
            <tr><td style="color:#64748b;padding-right:10px;text-align:right">Datum offerte</td><td style="text-align:right"><b>${fmtDate(o.datum)}</b></td></tr>
            <tr><td style="color:#64748b;padding-right:10px;text-align:right">Geldig tot</td><td style="text-align:right"><b>${fmtDate(o.geldigTot)}</b></td></tr>
          </table>
        </div>
      </div>

      <p style="margin-top:22px">${esc(o.contactAanhef || "")}</p>
      <p style="white-space:pre-wrap;color:#334155">${esc(o.introtekst || "")}</p>

      <div style="text-transform:uppercase;font-size:11px;letter-spacing:1px;color:#1C64B0;font-weight:800;margin:22px 0 10px">Product & prijs</div>
      ${blokken || '<p style="color:#94a3b8">Geen producten.</p>'}

      ${hide.totaal ? "" : `<div style="display:flex;justify-content:flex-end;margin-top:6px">
        <div style="background:#1C64B0;color:#fff;padding:10px 20px;border-radius:8px;font-size:16px;font-weight:800">Totaal: ${eur(totaal(o))} <span style="font-size:11px;font-weight:600;opacity:.8">excl. BTW</span></div>
      </div>`}

      ${hide.bijzonderheden ? "" : `<div style="text-transform:uppercase;font-size:11px;letter-spacing:1px;color:#1C64B0;font-weight:800;margin:22px 0 6px">Bijzonderheden</div>
      <ul style="margin:0;padding-left:18px;color:#334155;font-size:12.5px">${terms}</ul>`}

      <p style="margin-top:26px">Met vriendelijke groet,</p>

      <table style="width:100%;margin-top:20px;border-collapse:collapse">
        <tr>
          <td style="width:50%;vertical-align:bottom;padding-right:20px">
            <div style="text-transform:uppercase;font-size:10px;letter-spacing:1px;color:#94a3b8;font-weight:700;margin-bottom:6px">Namens ${esc(COMPANY.naam)}</div>
            ${sig}
            <div style="border-top:1px solid #94a3b8;margin-top:6px;padding-top:6px;font-size:12.5px"><b>${esc(o.ondertekenaar || "")}</b><br/><span style="color:#64748b">Accountmanager · ${fmtDate(o.datum)}</span></div>
          </td>
          <td style="width:50%;vertical-align:bottom;padding-left:20px">
            <div style="text-transform:uppercase;font-size:10px;letter-spacing:1px;color:#94a3b8;font-weight:700;margin-bottom:6px">Voor akkoord — klant</div>
            <div style="height:70px"></div>
            <div style="border-top:1px solid #94a3b8;margin-top:6px;padding-top:6px;font-size:12.5px"><span style="color:#64748b">Naam &amp; handtekening klant</span><br/><span style="color:#94a3b8">Datum: __________________</span></div>
          </td>
        </tr>
      </table>

      <hr style="margin-top:30px;border:none;border-top:1px solid #e2e8f0"/>
      <div style="font-size:10.5px;color:#94a3b8;margin-top:8px;text-align:center">${esc(COMPANY.naam)} &nbsp;·&nbsp; ${esc(COMPANY.adres)}, ${esc(COMPANY.postplaats)} &nbsp;·&nbsp; Tel ${esc(COMPANY.tel)} &nbsp;·&nbsp; ${esc(COMPANY.email)} &nbsp;·&nbsp; KvK ${esc(COMPANY.kvk)}</div>
      <script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };

  const openHide = (o) => { setHideOpts({ ...(o.verbergVelden || {}) }); setHideFor(o); };
  const toggleHide = (k) => setHideOpts((h) => ({ ...h, [k]: !h[k] }));
  const doDownload = async () => {
    const o = hideFor; if (!o) return;
    if (canEditOfferte(o)) {
      try { await api.put(`/sales/offertes/${o.id}`, { ...o, verbergVelden: hideOpts }); reload(); }
      catch (e) { toast.error(formatApiError(e)); }
    }
    const opts = hideOpts;
    setHideFor(null);
    print(o, opts);
  };

  const zichtbaar = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    if (!q) return offertes;
    return offertes.filter((o) => `${o.offertenummer || ""} ${o.klantNaam || ""}`.toLowerCase().includes(q));
  }, [offertes, zoek]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-800">Offertes</h2>
        <Input data-testid="offerte-zoek" placeholder="Zoek op offertenummer of klant…" value={zoek} onChange={(e) => setZoek(e.target.value)} className="w-64 max-w-full" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold text-slate-700">{editId ? `Offerte ${form.offertenummer} bewerken` : `Nieuwe offerte`} <span className="text-slate-400 font-normal">(nr. wordt {editId ? form.offertenummer : nextNummer(offertes)})</span></div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Klant</label>
            <Select value={form.klantCode || "__none__"} onValueChange={(v) => setForm({ ...form, klantCode: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="offerte-klant"><SelectValue placeholder="— kies klant —" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">— kies klant —</SelectItem>{klanten.map((k) => <SelectItem key={k.id} value={k.code}>{k.code} — {k.naam}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Aanhef</label><Input data-testid="offerte-aanhef" value={form.contactAanhef} onChange={(e) => setForm({ ...form, contactAanhef: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Datum</label><Input type="date" data-testid="offerte-datum" value={form.datum} onChange={(e) => setForm({ ...form, datum: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Geldig tot</label><Input type="date" data-testid="offerte-geldig" value={form.geldigTot} onChange={(e) => setForm({ ...form, geldigTot: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Status</label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="offerte-status"><SelectValue /></SelectTrigger><SelectContent>{OFFERTE_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Ondertekenaar (accountmanager)</label><Input data-testid="offerte-ondertekenaar" value={form.ondertekenaar} onChange={(e) => setForm({ ...form, ondertekenaar: e.target.value })} /></div>
        </div>
        {isPrivateAuthor && (
          <fieldset
            className="rounded-lg border border-amber-200 bg-amber-50 p-3"
            data-testid="offerte-visibility-selector"
          >
            <legend className="px-1 text-sm font-bold text-amber-950">
              Wie mag deze offerte zien?
            </legend>
            <p className="mb-2 text-xs text-amber-900">
              Jij en Bedrijfsleiding kunnen deze offerte altijd zien. Aangevinkte collega’s
              kunnen alleen bekijken en niet aanpassen.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {eligibleViewers.map((medewerker) => (
                <label
                  key={medewerker.id}
                  data-testid={`offerte-viewer-label-${medewerker.id}`}
                  className="flex cursor-pointer items-center gap-2 rounded border border-amber-100 bg-white px-2 py-1.5 text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    data-testid={`offerte-viewer-${medewerker.id}`}
                    checked={(form.allowed_viewer_ids || []).includes(medewerker.id)}
                    onChange={() => toggleViewer(medewerker.id)}
                    className="h-4 w-4 accent-[#1C64B0]"
                  />
                  <span>
                    {medewerker.name}
                    <span className="ml-1 text-xs text-slate-400">{medewerker.email}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Introtekst</label><Textarea data-testid="offerte-intro" rows={3} value={form.introtekst} onChange={(e) => setForm({ ...form, introtekst: e.target.value })} /></div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold text-slate-600">Productregels</label>
            <Button size="sm" variant="outline" data-testid="offerte-add-regel" onClick={addRegel}><Plus className="h-3.5 w-3.5 mr-1" /> Regel</Button>
          </div>
          <div className="space-y-3">
            {(form.regels || []).map((r, i) => {
              const basis = r.prijsBasis || "doos";
              return (
                <div key={i} data-testid="offerte-regel" className="rounded-md border border-slate-200 p-3 bg-slate-50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1"><ArtikelCombo artikelen={artikelen} value={r.artikel} onChange={(v) => setRegel(i, { artikel: v })} testid={`offerte-regel-artikel-${i}`} /></div>
                    <div className="text-right text-sm font-semibold text-slate-700 tabular-nums w-28" data-testid={`offerte-regel-totaal-${i}`}>{eur(regelTotaal(r))}</div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delRegel(i)}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4 text-xs">
                    <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">Folie</label>
                      <Select value={r.folie || "__none__"} onValueChange={(v) => setRegel(i, { folie: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid={`offerte-regel-folie-${i}`} className="h-9"><SelectValue placeholder="— folie —" /></SelectTrigger>
                        <SelectContent><SelectItem value="__none__">— folie —</SelectItem>{folieList.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">Prijsbasis</label>
                      <Select value={basis} onValueChange={(v) => setRegel(i, { prijsBasis: v })}>
                        <SelectTrigger data-testid={`offerte-regel-basis-${i}`} className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{PRIJSBASIS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">Stuks per doos</label><Input type="number" step="1" data-testid={`offerte-regel-stuks-${i}`} value={r.stuksPerDoos} onChange={(e) => setRegel(i, { stuksPerDoos: e.target.value })} className="h-9" /></div>
                    <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">Kg per stuk</label><Input type="number" step="0.001" data-testid={`offerte-regel-kgstuk-${i}`} value={r.kgPerStuk} onChange={(e) => setRegel(i, { kgPerStuk: e.target.value })} className="h-9" placeholder="bv. 1" /></div>

                    {basis === "kg" && <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">€ per kg</label><Input type="number" step="0.0001" data-testid={`offerte-regel-prijskg-${i}`} value={r.prijsPerKg} onChange={(e) => setRegel(i, { prijsPerKg: e.target.value })} className="h-9" /></div>}
                    {basis === "stuk" && <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">€ per stuk</label><Input type="number" step="0.0001" data-testid={`offerte-regel-prijsstuk-${i}`} value={r.prijsPerStuk} onChange={(e) => setRegel(i, { prijsPerStuk: e.target.value })} className="h-9" /></div>}
                    {basis === "doos" && <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">€ per doos</label><Input type="number" step="0.01" data-testid={`offerte-regel-prijsdoos-${i}`} value={r.prijsPerDoos} onChange={(e) => setRegel(i, { prijsPerDoos: e.target.value })} className="h-9" /></div>}

                    <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">Min. afname (pallets)</label><Input type="number" data-testid={`offerte-regel-pallets-${i}`} value={r.pallets} onChange={(e) => setRegel(i, { pallets: e.target.value })} className="h-9" placeholder="bv. 3" /></div>
                    <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">Dozen per pallet</label><Input type="number" data-testid={`offerte-regel-dpp-${i}`} value={r.dozenPerPallet} onChange={(e) => setRegel(i, { dozenPerPallet: e.target.value })} className="h-9" /></div>
                    <div><label className="block text-[11px] font-semibold text-slate-500 mb-1">Pallet</label><Input value={r.palletType} onChange={(e) => setRegel(i, { palletType: e.target.value })} className="h-9" /></div>
                    <div className="sm:col-span-3 lg:col-span-4"><label className="block text-[11px] font-semibold text-slate-500 mb-1">Opmerking</label><Input value={r.opmerking} onChange={(e) => setRegel(i, { opmerking: e.target.value })} className="h-9" /></div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                    <span>Netto/doos: <b className="text-slate-700">{kgf(nettoDoos(r))}</b></span>
                    <span>Netto/pallet: <b className="text-slate-700">{kgf(nettoPallet(r))}</b></span>
                    <span>Afname: <b className="text-slate-700" data-testid={`offerte-regel-dozen-${i}`}>{aantalDozen(r)} dozen</b></span>
                    <span>Doosprijs: <b className="text-[#1C64B0]" data-testid={`offerte-regel-doosprijs-${i}`}>{eur(doosPrijs(r))}</b></span>
                    <span>Prijs/kg: <b className="text-[#1C64B0]">{eur4(effKgPrijs(r))}</b></span>
                  </div>
                </div>
              );
            })}
            {(form.regels || []).length === 0 && <p className="text-sm text-slate-400">Nog geen regels — klik "Regel".</p>}
          </div>
          <div className="text-right font-bold text-slate-800 mt-2" data-testid="offerte-totaal">Totaal: {eur(totaal(form))} <span className="text-xs font-normal text-slate-400">excl. BTW</span></div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Bijzonderheden</label><Textarea data-testid="offerte-bijzonderheden" rows={4} value={form.bijzonderheden} onChange={(e) => setForm({ ...form, bijzonderheden: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Interne notitie (niet op PDF)</label><Textarea data-testid="offerte-notitie" rows={4} value={form.notitie} onChange={(e) => setForm({ ...form, notitie: e.target.value })} /></div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 inline-flex items-center gap-1"><PenLine className="h-3.5 w-3.5" /> Handtekening accountmanager (digitaal)</label>
          <div className="max-w-md"><SignaturePad key={sigKey} testid="offerte-signature-canvas" clearTestid="offerte-signature-clear" height={120} onChange={(sig) => setForm((f) => ({ ...f, handtekening: sig }))} /></div>
          <p className="text-[11px] text-slate-400 mt-1">Teken hierboven met de muis/touch. Deze handtekening komt op de offerte-PDF. De klant tekent met de hand op het geprinte document.</p>
        </div>

        <div className="flex gap-2">
          <Button data-testid="offerte-save" onClick={save} className="bg-[#1C64B0] hover:bg-[#154E8A]"><Plus className="h-4 w-4 mr-1" /> {editId ? "Offerte bijwerken" : "Offerte opslaan"}</Button>
          {editId && <Button variant="outline" onClick={nieuw}>Annuleren</Button>}
        </div>
      </div>

      <div className="space-y-2">
        {zichtbaar.length === 0 && <p className="text-sm text-slate-400">{offertes.length === 0 ? "Nog geen offertes." : "Geen offertes gevonden."}</p>}
        {zichtbaar.map((o) => (
          <div key={o.id} data-testid="offerte-row" className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-3">
            <Badge className="bg-[#1C64B0] text-white font-mono" data-testid="offerte-nr">{o.offertenummer || "—"}</Badge>
            <div className="flex-1 min-w-[180px]">
              <div className="font-semibold text-slate-800 inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /> {o.klantNaam || o.klantCode} <span className="text-slate-400 font-normal text-sm">· {fmtDate(o.datum)}</span></div>
              <div className="text-xs text-slate-500 mt-0.5">{(o.regels || []).length} regel(s) · Totaal {eur(o.totaal ?? totaal(o))}</div>
            </div>
            <Badge className={OFF_BADGE[o.status] || "bg-slate-100 text-slate-600"}>{o.status}</Badge>
            {o.visibility === "restricted" && <Badge data-testid="offerte-private-badge" className="bg-amber-100 text-amber-800">Privé</Badge>}
            {o.status === "Geaccepteerd" && canEditOfferte(o) && <Button variant="outline" size="sm" className="h-8" data-testid="offerte-maak-order" disabled={orderingId === o.id} onClick={() => maakOrder(o)}><Package className="h-4 w-4 mr-1" /> Maak order</Button>}
            <Button variant="ghost" size="icon" className="h-8 w-8" title="Download/print PDF" data-testid="offerte-print" onClick={() => openHide(o)}><Printer className="h-4 w-4 text-slate-500" /></Button>
            {canEditOfferte(o) && <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="offerte-edit" onClick={() => edit(o)}><Pencil className="h-4 w-4 text-slate-500" /></Button>}
            {canEditOfferte(o) && <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="offerte-delete" onClick={() => del(o)}><Trash2 className="h-4 w-4 text-slate-400" /></Button>}
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 inline-flex items-center gap-1"><FileText className="h-3 w-3" /> Tip: in het print-venster kies je "Opslaan als PDF" om de offerte te downloaden.</p>

      {hideFor && (
        <div data-testid="offerte-hide-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setHideFor(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-base font-bold text-slate-800">Offerte {hideFor.offertenummer} downloaden</h3>
              <p className="text-xs text-slate-500 mt-1">Vink aan wat je <b>niet</b> op de PDF wilt tonen (bijv. het totaalbedrag). Je keuze wordt bij deze offerte opgeslagen.</p>
            </div>
            <div className="space-y-2">
              {HIDE_FIELDS.map(([k, label]) => (
                <label key={k} data-testid={`offerte-hide-${k}`} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-[#1C64B0]" checked={!!hideOpts[k]} onChange={() => toggleHide(k)} />
                  Verberg: {label}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" data-testid="offerte-hide-cancel" onClick={() => setHideFor(null)}>Annuleren</Button>
              <Button className="bg-[#1C64B0] hover:bg-[#154E8A]" data-testid="offerte-hide-download" onClick={doDownload}><Printer className="h-4 w-4 mr-1" /> Opslaan &amp; downloaden</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
